import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import Button from '@/ui/components/shared/Button';
import { useTranslations } from 'next-intl';
import { InformationCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { twMerge } from 'tailwind-merge';
import { Option } from '@/ui/components/shared/multistepFormComponents/types';
import { ICFormAnswers } from '../types';

export type ICMultiChoiceSelectProps = {
  question: string;
  multipleChoice?: boolean;
  options: Option[];
  onSelect: (selectedValues?: string[]) => void;
  allowOtherInput?: boolean;
};

function ICMultiChoiceSelect({
  question,
  multipleChoice = true,
  options,
  onSelect,
  allowOtherInput = false,
}: ICMultiChoiceSelectProps) {
  const { setValue, watch } = useFormContext();
  const selectedValues: string[] | undefined = watch(question);
  const t = useTranslations('IC_Survey');
  const [openExtraInfo, setOpenExtraInfo] = useState<string | null>(null);
  const [otherText, setOtherText] = useState<string>('');

  const handleOptionToggle = (value: string) => {
    let updatedValues;

    if (multipleChoice) {
      if (selectedValues?.includes(value)) {
        updatedValues = selectedValues.filter((selected: string) => selected !== value);
      } else {
        updatedValues = selectedValues ? [...selectedValues, value] : [value];
      }
    } else {
      updatedValues = selectedValues?.includes(value) ? [] : [value];
    }

    setValue(question as keyof ICFormAnswers, updatedValues);

    if (value === 'other' && allowOtherInput && !selectedValues?.includes(value)) {
      setOtherText('');
    }
  };

  const handleSubmit = () => {
    // If "other" is selected and user typed something, use that text
    if (selectedValues?.includes('other') && allowOtherInput && otherText.trim()) {
      onSelect([...selectedValues.filter((val) => val !== 'other'), otherText]);
    } else {
      onSelect(selectedValues);
    }
  };

  const toggleExtraInfo = (value: string) => {
    setOpenExtraInfo(openExtraInfo === value ? null : value);
  };

  const isManyOptions = options.length > 6;

  return (
    <div className="flex flex-grow flex-col items-center justify-between">
      <div
        className={twMerge(
          'w-full max-w-lg',
          isManyOptions
            ? 'max-w-4xl columns-1 space-y-2 md:columns-2 lg:columns-3'
            : 'flex flex-col',
        )}
      >
        {options.map((option) => (
          <div key={option.value} className="mb-2 break-inside-avoid">
            <div
              data-id={option.value}
              onClick={() => handleOptionToggle(option.value)}
              className={twMerge(
                'flex w-full cursor-pointer items-center justify-between rounded-lg border p-3',
                selectedValues?.includes(option.value) ? 'border-primary-100' : 'border-neutral-10',
                'overflow-hidden text-ellipsis whitespace-nowrap',
              )}
            >
              <div className="flex items-center gap-4">
                {selectedValues?.includes(option.value) ? (
                  <CheckCircleIcon className="-mr-1 h-6 w-6 text-primary-100" />
                ) : (
                  <div className="h-5 w-5 rounded-full border" />
                )}
                {option.text}
              </div>

              {option.extraInfo && (
                <div>
                  <button
                    type="button"
                    className="rounded-full bg-primary-20"
                    onClick={(e) => {
                      toggleExtraInfo(option.value);
                      e.stopPropagation();
                    }}
                  >
                    <InformationCircleIcon className="-m-1 h-6 w-6 text-primary-100" />
                  </button>
                </div>
              )}
            </div>

            {openExtraInfo === option.value && (
              <div className="relative mt-2 rounded-lg bg-primary-20 px-4 py-3 pr-10">
                <button
                  type="button"
                  className="absolute right-1 top-1"
                  onClick={() => toggleExtraInfo(option.value)}
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
                <p>{option.extraInfo}</p>
              </div>
            )}
          </div>
        ))}

        {selectedValues?.includes('other') && allowOtherInput && (
          <div className="break-inside-avoid">
            <label htmlFor="otherText" className="sr-only block font-medium">
              {t('Please specify')}
            </label>
            <input
              id="otherText"
              placeholder={t('Please specify')}
              type="text"
              value={otherText}
              onChange={(e) => setOtherText(e.target.value)}
              className="block w-full rounded-md border border-neutral-10 bg-neutral-5 px-4 py-3 placeholder:text-neutral-50 focus:border-neutral-100 focus:outline-none focus:ring-neutral-100"
            />
          </div>
        )}
      </div>

      <Button
        className="mt-5 w-full max-w-lg"
        type="button"
        variant="yellowBlackBorder"
        disabled={!selectedValues || selectedValues.length === 0}
        onClick={handleSubmit}
      >
        {t('Next')}
      </Button>
    </div>
  );
}

export default ICMultiChoiceSelect;
